import { deleteField, onSnapshot } from "firebase/firestore";
import { networkError, notFound, permissionDenied, } from "@pack-it-up/shared";
// Latency-compensated snapshots surface a pending serverTimestamp() as null, which
// the mappers (rightly) reject as corrupt — the doc would vanish from the stream
// until the server ack. "estimate" substitutes a local best-guess Timestamp instead.
const snapshotOptions = { serverTimestamps: "estimate" };
// onSnapshot's error callback fires on things like a denied read (revoked grant, rule
// change) or a dropped listen stream — with no error channel on Observable<T> (T's
// consumers only ever see `next`), the alternative is Firestore's own uncaught-error
// log, which names neither the collection nor the query. `label` makes that log
// attributable instead of silent.
export const logSnapshotError = (label) => (error) => {
    console.error(`[onSnapshot:${label}] denied or dropped`, error);
};
// Wrap a query's onSnapshot into an Observable<T[]>. Each doc is validated through
// its mapper; a doc that fails validation is skipped (a Corrupt doc must not crash
// the whole stream). Cache-first emission is preserved — Firestore fires the local
// cache snapshot before the server round-trip (Doc 03 §3).
export const observeQuery = (q, toDomain, label) => ({
    subscribe(next) {
        const unsub = onSnapshot(q, (snap) => {
            const out = [];
            const rejected = [];
            for (const d of snap.docs) {
                const r = toDomain(d.data(snapshotOptions));
                if (r.ok)
                    out.push(r.value);
                else
                    rejected.push({ id: d.id, error: r.error });
            }
            // A doc that fails mapper validation is silently dropped from `out` by design
            // (a corrupt doc must not crash the whole stream) — but silent is exactly what
            // made the original swallowed-error bug hard to find, so log which ones and why.
            if (rejected.length > 0)
                console.error(`[observeQuery:${label}] ${rejected.length} doc(s) failed validation`, rejected);
            next(out);
        }, logSnapshotError(label));
        return unsub;
    },
});
export const observeDoc = (ref, toDomain, label) => ({
    subscribe(next) {
        const unsub = onSnapshot(ref, (snap) => {
            if (!snap.exists()) {
                next(null);
                return;
            }
            const r = toDomain(snap.data(snapshotOptions));
            next(r.ok ? r.value : null);
        }, logSnapshotError(label));
        return unsub;
    },
});
// Cross-user / privileged operations are backed by Cloud Functions (T12). Until then
// they reject — the contract suite runs the Firebase repos with `{ callables: false }`.
export const notImplemented = (method) => {
    throw new Error(`${method}: backed by a Cloud Function not yet implemented (T12)`);
};
// Field-level patch builder (Doc 03 §4): undefined = leave a field alone, null = clear
// it (Firestore `deleteField()` sentinel), a value = set it. Used to assemble
// updateDoc() payloads that touch only the changed fields. (The in-memory fake's
// `applyNullable` is the same three-state decision applied to a plain object instead
// of producing a Firestore sentinel.)
export const patchField = (obj, key, value) => {
    if (value === undefined)
        return;
    obj[key] = value === null ? deleteField() : value;
};
// Tags (T29) are stored absent rather than as an empty array, so an emptied tag list
// clears the field. Feed the result to `patchField`.
export const tagsPatch = (tags) => tags === undefined ? undefined : tags === null || tags.length === 0 ? null : tags;
// Map a Firestore SDK error to a DomainError — business logic never sees raw SDK codes.
export const toDomainError = (e) => {
    const code = e.code ?? "";
    switch (code) {
        case "permission-denied":
            return permissionDenied("perform this action");
        case "not-found":
            return notFound("document", "");
        case "unavailable":
        case "deadline-exceeded":
            return networkError(e);
        default:
            return networkError(e);
    }
};
//# sourceMappingURL=support.js.map